/**
 * Gets or creates the global AutoTracer console API.
 *
 * Side effects: may install `globalThis.autoTracer`.
 */
export function getOrCreateAutoTracerGlobalApi() {
    function isRecord(value) {
        return typeof value === "object" && value !== null;
    }
    function isOutputMode(value) {
        return value === "devtools" || value === "copy-paste";
    }
    function isAutoTracerGlobalApi(value) {
        if (!isRecord(value))
            return false;
        return (typeof value["getOutputMode"] === "function" &&
            typeof value["setOutputMode"] === "function");
    }
    function createNoopTracerRuntimeControls() {
        let isFilterModeEnabled = false;
        function start() {
            return;
        }
        function stop() {
            return;
        }
        function isEnabled() {
            return false;
        }
        function addFilter(_match) {
            return;
        }
        function showFilters() {
            return "exclude: { functions: [] }";
        }
        function clearFilters() {
            return;
        }
        function filterMode(enabled) {
            isFilterModeEnabled = enabled ?? true;
            return isFilterModeEnabled;
        }
        function setEnabledOnLoad(_value) {
            return;
        }
        function getEnabledOnLoad() {
            return false;
        }
        function setAutoStopTopLevel(_limit) {
            return;
        }
        function getAutoStopTopLevel() {
            return null;
        }
        function setAutoStopAll(_limit) {
            return;
        }
        function getAutoStopAll() {
            return null;
        }
        function getTopLevelCount() {
            return 0;
        }
        function getTotalCount() {
            return 0;
        }
        function resetCounts() {
            return;
        }
        function setStartTrigger(_pattern) {
            return;
        }
        function getStartTrigger() {
            return null;
        }
        function setEndTrigger(_pattern) {
            return;
        }
        function getEndTrigger() {
            return null;
        }
        function setEndTriggerMode(_mode) {
            return;
        }
        function getEndTriggerMode() {
            return "on-exit";
        }
        function setTriggerRearmMode(_mode) {
            return;
        }
        function getTriggerRearmMode() {
            return "once";
        }
        function clearAllTriggers() {
            return;
        }
        return {
            start,
            stop,
            isEnabled,
            addFilter,
            showFilters,
            clearFilters,
            filterMode,
            setEnabledOnLoad,
            getEnabledOnLoad,
            setAutoStopTopLevel,
            getAutoStopTopLevel,
            setAutoStopAll,
            getAutoStopAll,
            getTopLevelCount,
            getTotalCount,
            resetCounts,
            setStartTrigger,
            getStartTrigger,
            setEndTrigger,
            getEndTrigger,
            setEndTriggerMode,
            getEndTriggerMode,
            setTriggerRearmMode,
            getTriggerRearmMode,
            clearAllTriggers,
        };
    }
    function getOrCreateInternalState() {
        if (globalThis.__autoTracerInternal !== undefined) {
            return globalThis.__autoTracerInternal;
        }
        globalThis.__autoTracerInternal = {
            outputMode: "devtools",
            subscribers: [],
        };
        return globalThis.__autoTracerInternal;
    }
    const existing = globalThis.autoTracer;
    if (isAutoTracerGlobalApi(existing)) {
        getOrCreateInternalState();
        return existing;
    }
    const state = getOrCreateInternalState();
    /**
     * Gets the current canonical outputMode.
     *
     * @returns Current outputMode
     */
    function getOutputMode() {
        return state.outputMode;
    }
    /**
     * Sets the canonical outputMode and notifies subscribers.
     *
     * Side effects: notifies registered subscribers.
     *
     * @param mode - New output mode
     */
    function setOutputMode(mode) {
        if (!isOutputMode(mode))
            return;
        state.outputMode = mode;
        state.subscribers.forEach((subscriber) => {
            subscriber(mode);
        });
    }
    const api = {
        getOutputMode,
        setOutputMode,
        flowTracer: createNoopTracerRuntimeControls(),
    };
    if (isRecord(existing)) {
        Object.keys(existing).forEach((key) => {
            if (!(key in api)) {
                Reflect.set(api, key, Reflect.get(existing, key));
            }
        });
    }
    globalThis.autoTracer = api;
    return api;
}
